import { useState, useMemo } from 'react';
import { V1 } from '@frontend/api-frontend-gateway';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Table, Tabs, TextLink, Button } from '@frontend/design-system';

const sitesProd = [
  { stableDomain: 'app.getweave.com', edgeDomain: 'edge.getweave.com' },
  { stableDomain: 'edge.getweave.com' },
  { stableDomain: 'auth.getweave.com' },
  { stableDomain: 'notification.getweave.com' },
  { stableDomain: 'weavebillpay.com' },
  { stableDomain: 'book2.getweave.com' },
  { stableDomain: 'digital-forms.getweave.com' },
  { stableDomain: 'pds.weavelab.ninja' },
  { stableDomain: 'integration-dashboard.getweave.com' },
  { stableDomain: 'wam2.weavelab.ninja' },
  { stableDomain: 'storybook.weavelab.ninja' },
  { stableDomain: 'deployment-playground.weavelab.ninja', edgeDomain: 'deployment-playground-edge.weavelab.ninja' },
  { stableDomain: 'deployment-playground-edge.weavelab.ninja' },
];

const sitesDev = [
  { stableDomain: 'app.weavedev.net' },
  { stableDomain: 'weave-air.weavedev.net' },
  { stableDomain: 'auth.weavedev.net' },
  { stableDomain: 'notification.weavedev.net' },
  { stableDomain: 'pay.weavedev.net' },
  { stableDomain: 'book2.weavedev.net' },
  { stableDomain: 'digital-forms.weavedev.net' },
  { stableDomain: 'forms.weavedev.net' },
  { stableDomain: 'pds.weavedev.net' },
  { stableDomain: 'integration-dashboard.weavedev.net' },
  { stableDomain: 'wam2.weavedev.net' },
  { stableDomain: 'storybook.weavedev.net' },
];

const useSitesData = (tag: string) => {
  const result = V1.Queries.useListSiteConfigsQuery({
    request: {
      tag,
    },
    options: {
      retry: 2,
    },
  });

  return result;
};

const DEFAULT_TAB = 'prod-tab';

const normalizeSiteName = (siteName: string) =>
  siteName
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '')
    .toLowerCase();

export const SitesList = ({ onSiteDetailsClick }: { onSiteDetailsClick?: (siteName: string) => void } = {}) => {
  const [activeTab, setActiveTab] = useState<string>(DEFAULT_TAB);

  // Determine the tag based on the active tab
  const currentTag = activeTab === 'dev-tab' ? 'dev' : 'prod';

  const { data: sites } = useSitesData(currentTag);

  // Get the appropriate sites array based on the current tab
  const targetSites = activeTab === 'dev-tab' ? sitesDev : sitesProd;

  // Filter and order sites based on the target sites whitelist
  const filteredAndOrderedSites = useMemo(() => {
    const siteConfigs = sites?.siteConfigs || [];

    return siteConfigs
      .filter((site) => {
        const siteName = site.siteName;
        if (!siteName) return false;
        return targetSites.some(
          (targetSite) => normalizeSiteName(targetSite.stableDomain) === normalizeSiteName(siteName)
        );
      })
      .sort((a, b) => {
        const aSiteName = a.siteName;
        const bSiteName = b.siteName;
        const indexA = aSiteName
          ? targetSites.findIndex(
              (targetSite) => normalizeSiteName(targetSite.stableDomain) === normalizeSiteName(aSiteName)
            )
          : -1;
        const indexB = bSiteName
          ? targetSites.findIndex(
              (targetSite) => normalizeSiteName(targetSite.stableDomain) === normalizeSiteName(bSiteName)
            )
          : -1;
        return indexA - indexB;
      });
  }, [sites?.siteConfigs, targetSites]);

  return (
    <div>
      <Tabs initialTab={activeTab} onChange={setActiveTab}>
        <Tabs.Bar fullWidth css={{ marginBottom: theme.spacing(2) }}>
          <Tabs.Tab id='prod-tab'>Production</Tabs.Tab>
          <Tabs.Tab id='dev-tab'>Development</Tabs.Tab>
        </Tabs.Bar>
        <Tabs.Panel controller='prod-tab'>
          <SitesTable
            sites={filteredAndOrderedSites}
            targetSites={targetSites}
            onSiteDetailsClick={onSiteDetailsClick}
          />
        </Tabs.Panel>
        <Tabs.Panel controller='dev-tab'>
          <SitesTable
            sites={filteredAndOrderedSites}
            targetSites={targetSites}
            onSiteDetailsClick={onSiteDetailsClick}
          />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

type Props = {
  sites: NonNullable<V1.Types.ListSiteConfigsIO['output']['siteConfigs']>;
  targetSites: Array<{ stableDomain: string; edgeDomain?: string }>;
  onSiteDetailsClick?: (siteName: string) => void;
};
const SitesTable = ({ sites, onSiteDetailsClick }: Props) => {
  return (
    <Table
      data={sites ?? []}
      clientPaginationConfig={{
        defaultRowsPerPage: 25,
      }}
      colConfig={[
        {
          Header: 'Site Name',
          id: 'siteName',
          accessor: (site) => site.siteName,
          minWidth: 250,
          cellRenderer: onSiteDetailsClick
            ? (siteName) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                  <TextLink onClick={() => onSiteDetailsClick(siteName)} style={{ cursor: 'pointer' }}>
                    {siteName}
                  </TextLink>
                  <Button
                    iconName='external-link-small'
                    variant='tertiary'
                    label={`Open ${siteName} in new tab`}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      window.open(`https://${siteName}`, '_blank', 'noopener,noreferrer');
                    }}
                    size='small'
                  />
                </div>
              )
            : undefined,
        },
        {
          Header: 'Version',
          id: 'version',
          accessor: (site) => (
            <TextLink
              href={`https://github.com/weave-lab/frontend/commit/${site.version}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {site.version?.substring(0, 8) || 'N/A'}
            </TextLink>
          ),
          minWidth: 100,
        },
        {
          Header: 'Published At',
          id: 'publishedAt',
          accessor: (site) => {
            if (!site.publishedAt) return 'N/A';
            return new Date(site.publishedAt).toLocaleString();
          },
          minWidth: 180,
        },
        {
          Header: 'Tags',
          id: 'tags',
          accessor: (site) => site.tags?.join(', ') || 'N/A',
          maxWidth: 100,
        },
        {
          Header: 'Urgency',
          id: 'urgency',
          accessor: (site) => site.publicMetadata?.urgency || 'N/A',
          maxWidth: 80,
        },
        {
          Header: 'Bucket Prefix',
          id: 'bucketPrefix',
          accessor: (site) => (
            <TextLink
              href={`https://console.cloud.google.com/storage/browser/${site.bucketName}/${site.bucketPrefix}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {site.bucketPrefix || 'N/A'}
            </TextLink>
          ),
          minWidth: 200,
        },
      ]}
      rowActions={{
        actions: [
          {
            Icon: () => <Icon name='arrow-right-small' />,
            label: 'View Details',
            onClick: (site: any) => {
              if (site.siteName && onSiteDetailsClick) {
                onSiteDetailsClick(site.siteName);
              }
            },
          },
        ],
      }}
      isPaginated
      hasGlobalSearch
      tableActions={[
        {
          Icon: () => <Icon name='update' />,
          label: 'Refresh',
          onClick: () => {
            window.location.reload();
          },
        },
      ]}
    />
  );
};
